import React, { useState, useEffect, useRef, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Menu,
    ListItemIcon,
    ListItemText,
    Grid,
    InputAdornment
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Clear as ClearIcon,
    GetApp as GetAppIcon,
    Description as DescriptionIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import toastr from 'toastr';
import * as LegacyViewerService from '../../../services/clinicPortalServices/legacyViewerService';
import { formatBytes, formatNumber } from '../../../services/common/util';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import CustomHeaderComponent from './CustomHeaderComponent';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

const EntityDataViewer = ({ entity, onBack }) => {
    const gridRef = useRef();
    const { themeName } = useContext(ThemeContext);
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileData, setFileData] = useState(null);
    const [rowData, setRowData] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [statistics, setStatistics] = useState(null);

    // AG-Grid default column definition
    const defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 100,
        flex: 1
    };

    // Components for AG-Grid
    const components = {
        agColumnHeader: CustomHeaderComponent
    };

    useEffect(() => {
        if (entity) {
            loadEntityFiles();
        }
    }, [entity]);

    useEffect(() => {
        if (selectedFile) {
            loadFileData();
        }
    }, [selectedFile, filters]);

    const loadEntityFiles = async () => {
        try {
            setLoading(true);
            const response = await LegacyViewerService.getEntityFiles(entity.entityType);
            if (response.success) {
                setFiles(response.data);
                if (response.data.length > 0) {
                    setSelectedFile(response.data[0]);
                }
            }
        } catch (error) {
            toastr.error(`Failed to load entity files: ${error.message || 'Unknown error'}`);
            console.error('Load entity files error:', error);
            console.error('Entity type:', entity?.entityType);
        } finally {
            setLoading(false);
        }
    };

    const loadFileData = async () => {
        try {
            setLoading(true);
            const response = await LegacyViewerService.readCSVData(entity.entityType, selectedFile.fileName, {
                // Load all data without pagination limit
                useDatabase: true,
                limit: 1000000, // Load up to 1 million rows
                ...filters
            });

            if (response.success) {
                setFileData(response.data);

                // Set up column definitions based on headers
                if (response.data.headers && response.data.headers.length > 0) {
                    const columns = [
                        {
                            headerName: 'Row #',
                            field: '_rowIndex',
                            width: 100,
                            pinned: 'left',
                            filter: false
                        },
                        ...response.data.headers.map((header) => ({
                            headerName: header,
                            field: header,
                            filter: 'agTextColumnFilter',
                            filterParams: {
                                filterOptions: [
                                    'contains',
                                    'notContains',
                                    'equals',
                                    'notEqual',
                                    'startsWith',
                                    'endsWith'
                                ],
                                defaultOption: 'contains'
                            }
                        }))
                    ];
                    setColumnDefs(columns);
                }

                // Set row data with row index
                if (response.data.rows) {
                    const rowsWithIndex = response.data.rows.map((row, index) => ({
                        ...row,
                        _rowIndex: row._rowIndex || index + 1
                    }));
                    setRowData(rowsWithIndex);
                }
            }
        } catch (error) {
            toastr.error('Failed to load file data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadFileStatistics = async (fileName) => {
        try {
            const response = await LegacyViewerService.getFileStatistics(fileName);
            if (response.success) {
                setStatistics(response.data.statistics);
            }
        } catch (error) {
            console.error('Failed to load file statistics');
        }
    };

    const handleFileChange = (event) => {
        const file = files.find((f) => f.fileName === event.target.value);
        setSelectedFile(file);
        setPage(0);
        setFilters({});
        setSearchTerm('');
        if (file) {
            loadFileStatistics(file.fileName);
        }
    };

    const onGridReady = (params) => {
        // Store grid API reference
        gridRef.current = params.api;
    };

    const onPaginationChanged = () => {
        // AG-Grid handles pagination internally
        // No need to sync with React state
    };

    const onFilterChanged = () => {
        if (gridRef.current) {
            const filterModel = gridRef.current.getFilterModel();
            const newFilters = {};

            Object.entries(filterModel).forEach(([field, filter]) => {
                if (filter && filter.filter) {
                    newFilters[field] = filter.filter;
                }
            });

            setFilters(newFilters);
            setPage(0);
        }
    };

    const handleSearchInFile = async () => {
        if (!searchTerm) return;

        try {
            setLoading(true);
            const response = await LegacyViewerService.searchInFile(
                entity.entityType,
                selectedFile.fileName,
                searchTerm,
                100,
                true // useDatabase
            );

            if (response.success) {
                toastr.success(`Found ${response.data.length} matches`);
                // Convert search results to row data
                if (response.data.length > 0) {
                    const searchRows = response.data.map((r, index) => ({
                        ...r.data,
                        _searchMatch: true,
                        _rowIndex: r.rowIndex || index + 1
                    }));
                    setRowData(searchRows);

                    // Apply quick filter to highlight search term
                    if (gridRef.current) {
                        gridRef.current.setQuickFilter(searchTerm);
                    }
                }
            }
        } catch (error) {
            toastr.error('Search failed');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (format) => {
        try {
            if (gridRef.current) {
                if (format === 'csv') {
                    // Use AG-Grid's built-in CSV export
                    gridRef.current.exportDataAsCsv({
                        fileName: selectedFile.fileName,
                        columnKeys: columnDefs.map((col) => col.field)
                    });
                } else {
                    // For JSON, export the current data
                    const rowData = [];
                    gridRef.current.forEachNode((node) => rowData.push(node.data));

                    const blob = new Blob([JSON.stringify(rowData, null, 2)], { type: 'application/json' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = selectedFile.fileName.replace('.csv', '.json');
                    a.click();
                    window.URL.revokeObjectURL(url);
                }

                toastr.success('Export completed');
            }
        } catch (error) {
            toastr.error('Export failed');
            console.error(error);
        }
        setAnchorEl(null);
    };

    const handleFilterChange = (column, value) => {
        const newFilters = { ...filters };
        if (value) {
            newFilters[column] = value;
        } else {
            delete newFilters[column];
        }
        setFilters(newFilters);
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({});
        setSearchTerm('');
        setPage(0);

        // Clear AG-Grid filters
        if (gridRef.current) {
            gridRef.current.setFilterModel(null);
            gridRef.current.setQuickFilter('');
        }
    };

    const hasActiveFilters = Object.keys(filters).length > 0 || searchTerm;

    return (
        <Box>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
                    {entity.entityType.replace(/_/g, ' ')} Data
                </Typography>
            </Box>

            {/* File Selection and Actions */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Select File</InputLabel>
                        <Select value={selectedFile?.fileName || ''} onChange={handleFileChange} label="Select File">
                            {files.map((file) => (
                                <MenuItem key={file.fileName} value={file.fileName}>
                                    {file.fileName} ({formatNumber(file.rowCount)} rows)
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box display="flex" gap={1}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search in file..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchInFile()}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearchInFile}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            <MenuItem onClick={() => handleExport('csv')}>
                                <ListItemIcon>
                                    <DescriptionIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Export as CSV</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => handleExport('json')}>
                                <ListItemIcon>
                                    <GetAppIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Export as JSON</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Grid>
            </Grid>

            {/* Active Filters */}
            {hasActiveFilters && (
                <Box mb={2} display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                        Active filters:
                    </Typography>
                    {Object.entries(filters).map(([key, value]) => (
                        <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            onDelete={() => handleFilterChange(key, '')}
                            size="small"
                        />
                    ))}
                    {searchTerm && (
                        <Chip
                            label={`Search: ${searchTerm}`}
                            onDelete={() => setSearchTerm('')}
                            size="small"
                            color="primary"
                        />
                    )}
                    <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
                        Clear All
                    </Button>
                </Box>
            )}

            {/* Data Table */}
            <Paper sx={{ width: '100%', height: 'calc(100vh - 400px)', minHeight: '500px' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : selectedFile ? (
                    <div id="myGrid" style={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            components={components}
                            pagination={true}
                            paginationPageSize={rowsPerPage}
                            paginationPageSizeSelector={[50, 100, 250, 500]}
                            onGridReady={onGridReady}
                            onPaginationChanged={onPaginationChanged}
                            onFilterChanged={onFilterChanged}
                            theme={themeName === 'Light' ? themeLight : themeDark}
                            enableCellTextSelection={true}
                            ensureDomOrder={true}
                            suppressMenuHide={true}
                            rowSelection="multiple"
                            animateRows={true}
                            quickFilterText={searchTerm}
                            domLayout="normal"
                        />
                    </div>
                ) : (
                    <Alert severity="info" sx={{ m: 2 }}>
                        Select a file to view data
                    </Alert>
                )}
            </Paper>

            {/* File Statistics */}
            {statistics && (
                <Paper sx={{ mt: 3, p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Column Statistics
                    </Typography>
                    <Grid container spacing={2}>
                        {Object.entries(statistics.nullCounts || {})
                            .slice(0, 6)
                            .map(([column, nullCount]) => (
                                <Grid item xs={12} sm={6} md={4} key={column}>
                                    <Box>
                                        <Typography variant="subtitle2">{column}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Nulls: {nullCount} | Unique: {statistics.uniqueCounts?.[column] || 0} |
                                            Types: {statistics.dataTypes?.[column]?.join(', ') || 'unknown'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                    </Grid>
                </Paper>
            )}
        </Box>
    );
};

export default EntityDataViewer;
